import { useEffect, useId, useState, type FormEvent } from "react";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@libs/zapi-ui/ds/components/dialog";
import { Field, FieldError, FieldLabel } from "@libs/zapi-ui/ds/components/field";
import { Input } from "@libs/zapi-ui/ds/components/input";
import { useTranslation } from "react-i18next";

type TProps = {
  isOpen: boolean;
  title: string;
  confirmText: string;
  initialName?: string;
  isSubmitting?: boolean;
  errorMessage?: string | null;
  onClose: () => void;
  onConfirm: (name: string) => void;
};

export function NamePromptModal({
  isOpen,
  title,
  confirmText,
  initialName = "",
  isSubmitting,
  errorMessage,
  onClose,
  onConfirm
}: TProps) {
  const { t } = useTranslation(["projects", "common"]);
  const [name, setName] = useState(initialName);
  const nameId = useId();

  useEffect(() => {
    if (isOpen) setName(initialName);
  }, [isOpen, initialName]);

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && !isSubmitting;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onConfirm(trimmed);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent onClick={(e) => e.stopPropagation()} closeLabel={t("common:close")} className="w-[420px]">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>
              <span className="block typo-h6 text-innoveraZinc-900">{title}</span>
            </DialogTitle>
          </DialogHeader>
          <Field data-invalid={!!errorMessage || undefined} className="w-full gap-2">
            <FieldLabel htmlFor={nameId}>{t("projects:savedFilters.nameLabel")}</FieldLabel>
            <Input
              id={nameId}
              data-testid="name-prompt-modal-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("projects:savedFilters.namePlaceholder")}
              aria-invalid={!!errorMessage || undefined}
              maxLength={255}
            />
            {errorMessage && <FieldError>{errorMessage}</FieldError>}
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" data-testid="name-prompt-modal-cancel-button" onClick={onClose}>
              {t("common:cancel")}
            </Button>
            <Button type="submit" data-testid="name-prompt-modal-confirm-button" disabled={!canSubmit}>
              {confirmText}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
